// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { type MouseEvent, useMemo } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Sprout } from "lucide-react"
import type { ColumnDef } from "@tanstack/react-table"
import SmartTable from "@/components/ui/SmartTable"
import { useCycleLatestSnapshot } from "@/queries/queries.eventJournal"
import { useEvolutionCycle } from "@/queries/queries.evolutionCycles"
import { useTableUrlState } from "@/hooks/useTableUrlState"
import { parseCycleSnapshotPayload } from "../_snapshotPayload"

// The leaderboard's sortable column ids, fed to the shared URL-state codec so
// a hostile `?lb_sort=` decodes against the real column set (ADR-0006).
const LEADERBOARD_COLUMN_IDS = [
  "agent_uuid",
  "best_score",
  "first_iter",
  "last_iter",
  "n_evaluations",
  "n_evaluation_errors",
  "error_rate",
  "median_evaluation_cost",
  "n_children",
] as const

/**
 * Candidate leaderboard panel (PRD §M9 §User Stories §7, slice 10).
 *
 * Every evaluated agent is a *candidate*; the panel header carries the
 * candidate count. The top `n_best_agents` candidates by score (already
 * the leading rows since the table pre-sorts by best_score DESC, with no
 * zero floor — fitness is higher-is-better on any sign) are the agents
 * submitted to the ecosystem arena at
 * cycle end (`genetic_evolver_engine.submit_arena_entries` → the
 * `n_best_agents` result agents), so they carry a `Sprout` picto with a
 * "Promoted to the ecosystem" label. `n_best_agents` is null for a
 * still-running or pre-feature cycle ⇒ no marking (never fabricated).
 *
 * Renders a `SmartTable` with one row per `per_agent_rollup` entry of
 * the latest `EvolutionCycleSnapshotEvent` for the current cycle. The
 * iso column set mirrors the legacy ebiose-webapp cycle dashboard:
 *
 *   - agent UUID — truncated, click-to-copy the full UUID.
 *   - best_score — numeric, 3 decimals.
 *   - first_iter / last_iter — int.
 *   - n_evaluations (UI label "Runs") — per-agent total evaluation
 *     count **including errored runs** (INV-6).
 *   - n_evaluation_errors — int.
 *   - error rate — derived `n_evaluation_errors / n_evaluations`,
 *     formatted as a percentage. Shows `—` when `n_evaluations == 0`
 *     so no NaN / Infinity leaks into the DOM.
 *   - median_evaluation_cost — currency.
 *   - n_children — int.
 *
 * Rows are pre-sorted by `best_score DESC` so the first render reflects
 * the leaderboard's primary ordering invariant (issue AC3 ; the
 * SmartTable's column-click sort can re-order from there).
 *
 * Data plane invariants applied:
 *   - **INV-1** — the panel renders nothing when the snapshot is null
 *     (parent shell owns the US §14 error state).
 *   - **INV-2** — data exclusively from `CycleSnapshotDto.per_agent_rollup`
 *     in the snapshot's `payload_json`. No client-side merge from per-iter
 *     events.
 *   - **INV-6** — `n_evaluations` includes errored runs.
 */

const truncateUuid = (uuid: string): string =>
  uuid.length > 8 ? `${uuid.slice(0, 8)}…` : uuid

const formatScore = (n: number): string => n.toFixed(3)
const formatCost = (n: number): string => `$${n.toFixed(4)}`

const formatErrorRate = (
  nEvaluations: number,
  nErrors: number,
): string => {
  // Defensive: rollup semantics should never produce n_evaluations == 0,
  // but the UI must not leak NaN / Infinity if it ever does (AC6).
  if (nEvaluations <= 0) return "—"
  const rate = nErrors / nEvaluations
  return `${(rate * 100).toFixed(1)}%`
}

const AgentUuidCell = ({ uuid }: { uuid: string }) => {
  const onClick = (e: MouseEvent) => {
    // The row navigates to the agent-detail route; click-to-copy must not
    // also trigger that navigation, so we stop the click from bubbling to
    // the row handler (same affordance split as the My Cycles id cell).
    e.stopPropagation()
    // navigator.clipboard is jsdom-friendly when the test seam stubs
    // `writeText` via `vi.spyOn(navigator.clipboard, "writeText")`. We
    // do not surface a "copied" toast — keep the UX minimal until a
    // future iteration of the design layer adds the affordance.
    void navigator.clipboard?.writeText(uuid)
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Copy agent UUID ${uuid}`}
      className="font-mono text-xs underline decoration-dotted cursor-pointer"
      title={uuid}
    >
      {truncateUuid(uuid)}
    </button>
  )
}

/**
 * Marks a candidate promoted into the ecosystem at cycle end — a
 * `Sprout` picto (the evolution/ecosystem metaphor) labelled for AT
 * users. Hover title + `aria-label` both read "Promoted to the
 * ecosystem"; the icon itself is `aria-hidden` so the single accessible
 * name comes from the wrapping `role="img"` span.
 */
const PromotedBadge = () => (
  <span
    role="img"
    aria-label="Promoted to the ecosystem"
    title="Promoted to the ecosystem"
    className="inline-flex shrink-0 text-primary"
  >
    <Sprout aria-hidden className="size-3.5" />
  </span>
)

export interface LeaderboardProps {
  cycleId?: string
}

export default function Leaderboard({ cycleId }: LeaderboardProps = {}) {
  const params = useParams<{ cycleId: string }>()
  const navigate = useNavigate()
  const resolvedCycleId = cycleId ?? params.cycleId
  const snapshotQuery = useCycleLatestSnapshot(resolvedCycleId)
  // Cycle details carry `n_candidates` (the header count) and
  // `n_best_agents` (how many were promoted to the ecosystem). The hook
  // is already fetched by the shell's sibling panels, so this reuses the
  // React-Query cache — no extra request in production.
  const cycleQuery = useEvolutionCycle(resolvedCycleId)

  // Deep-linkable sort + page state (ADR-0006): both live in the URL via the
  // shared codec binding — the same one My Forges and My Cycles use — under
  // `lb_sort` / `lb_page`, namespaced so they never collide with the events
  // panel's own params on the same dashboard route. Missing / invalid values
  // fall back to no-sort / page 1 (SmartTable also clamps out-of-range pages).
  const { sorting, onSortingChange, pageIndex, onPageIndexChange } =
    useTableUrlState({
      columnIds: LEADERBOARD_COLUMN_IDS,
      sortParam: "lb_sort",
      pageParam: "lb_page",
    })

  const rollup = useMemo<AgentCumulativeRollup[]>(() => {
    if (!snapshotQuery.data) return []
    const parsed = parseCycleSnapshotPayload<CycleSnapshotDto>(
      snapshotQuery.data.payload_json,
    )
    const arr = parsed?.per_agent_rollup ?? []
    // Pre-sort by best_score DESC so first render satisfies the
    // leaderboard's default ordering (AC3). SmartTable's column-click
    // sort can re-order from there.
    return [...arr].sort((a, b) => b.best_score - a.best_score)
  }, [snapshotQuery.data])

  const nBestAgents = cycleQuery.data?.n_best_agents ?? null
  const nCandidates = cycleQuery.data?.n_candidates ?? null

  // The promoted candidates = the top `n_best_agents` rows of the
  // `best_score DESC` rollup — no comparison to zero, because fitness is
  // higher-is-better on any sign, so no zero floor gates promotion.
  // `rollup` is already best_score DESC, so the winners are its leading
  // entries. Keyed by uuid so the marking follows an agent even after a
  // column re-sort. Null `n_best_agents` (running / pre-feature cycle) ⇒
  // empty set ⇒ no marking (never fabricated).
  const promotedUuids = useMemo<Set<string>>(() => {
    if (nBestAgents == null) return new Set()
    return new Set(
      rollup.slice(0, nBestAgents).map((r) => r.agent_uuid),
    )
  }, [rollup, nBestAgents])

  // While the snapshot read is still in flight, render nothing (no
  // premature zero-state flash) — the panel only commits to a state once
  // its own source has resolved.
  if (snapshotQuery.isLoading) {
    return null
  }

  // Per-panel gating (issue `per-panel-gating-t0`): before the first
  // snapshot the rollup is empty, so the panel falls through to its own
  // "no agents yet" zero-state — never a blank node, never a fabricated
  // row. `rollup` is `[]` whenever the snapshot is absent (the useMemo
  // above returns `[]` when `snapshotQuery.data` is nullish).
  if (rollup.length === 0) {
    return (
      <section
        role="region"
        aria-label="Agent leaderboard panel"
        className="box flex flex-col gap-3.5 p-5"
      >
        <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-text-tertiary">
          Candidate leaderboard
        </span>
        <div className="text-sm text-text-tertiary">No agents to show yet.</div>
      </section>
    )
  }

  const columns: ColumnDef<AgentCumulativeRollup>[] = [
    {
      id: "agent_uuid",
      header: "Agent",
      accessorFn: (r) => r.agent_uuid,
      cell: ({ row }) => (
        <span className="flex items-center gap-1.5">
          {promotedUuids.has(row.original.agent_uuid) && <PromotedBadge />}
          <AgentUuidCell uuid={row.original.agent_uuid} />
        </span>
      ),
    },
    {
      id: "best_score",
      header: "Best score",
      accessorFn: (r) => r.best_score,
      cell: ({ row }) => formatScore(row.original.best_score),
    },
    {
      id: "first_iter",
      header: "First iter",
      accessorFn: (r) => r.first_iter,
      cell: ({ row }) => row.original.first_iter,
    },
    {
      id: "last_iter",
      header: "Last iter",
      accessorFn: (r) => r.last_iter,
      cell: ({ row }) => row.original.last_iter,
    },
    {
      id: "n_evaluations",
      header: "Runs",
      accessorFn: (r) => r.n_evaluations,
      cell: ({ row }) => row.original.n_evaluations,
    },
    {
      id: "n_evaluation_errors",
      header: "Errors",
      accessorFn: (r) => r.n_evaluation_errors,
      cell: ({ row }) => row.original.n_evaluation_errors,
    },
    {
      id: "error_rate",
      header: "Error rate",
      enableSorting: false,
      cell: ({ row }) =>
        formatErrorRate(
          row.original.n_evaluations,
          row.original.n_evaluation_errors,
        ),
    },
    {
      id: "median_evaluation_cost",
      header: "Median cost",
      accessorFn: (r) => r.median_evaluation_cost,
      cell: ({ row }) => formatCost(row.original.median_evaluation_cost),
    },
    {
      id: "n_children",
      header: "Children",
      accessorFn: (r) => r.n_children,
      cell: ({ row }) => row.original.n_children,
    },
  ]

  return (
    <section
      role="region"
      aria-label="Agent leaderboard panel"
      className="box flex flex-col gap-3.5 p-5"
    >
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
        <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-text-tertiary">
          Candidate leaderboard
        </span>
        <span className="flex items-center gap-3 text-[11.5px] text-text-muted">
          {promotedUuids.size > 0 && (
            <span className="flex items-center gap-1 text-primary">
              <Sprout aria-hidden className="size-3" />
              promoted to ecosystem
            </span>
          )}
          <span>
            {nCandidates ?? rollup.length} candidates · sorted by best_score
          </span>
        </span>
      </div>
      <SmartTable
        data={rollup}
        columns={columns}
        pageSize={10}
        sorting={sorting}
        onSortingChange={onSortingChange}
        pageIndex={pageIndex}
        onPageIndexChange={onPageIndexChange}
        onRowClick={(r) =>
          // The agent-detail route (`/agents/:agentId`) exists and
          // degrades gracefully (its own not-found state) for any id, so
          // every row links — no dead-link pre-check is possible client
          // -side without an N-request probe (PRD §Further Notes).
          navigate(`/agents/${r.agent_uuid}`)
        }
      />
    </section>
  )
}
